package ru.chm.gradereport.ui.screens.course.sheet

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import ru.chm.gradereport.model.LessonSessionDto
import ru.chm.gradereport.ui.components.AppCard
import ru.chm.gradereport.ui.components.AppTextField
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.PrimaryButton
import ru.chm.gradereport.ui.components.SecondaryButton
import ru.chm.gradereport.ui.components.formatDisplayDate
import ru.chm.gradereport.ui.components.horizontalScrollOnWheel
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.YellowAccent

@Composable
internal fun AddHomeworkDialog(
    sessions: List<LessonSessionDto>,
    initialSessionId: String?,
    onDismiss: () -> Unit,
    onConfirm: (sessionId: String, title: String, description: String, maxScore: Double) -> Unit,
) {
    var selectedSessionId by remember(sessions, initialSessionId) {
        mutableStateOf(initialSessionId ?: sessions.maxByOrNull { it.date }?.id)
    }
    var title by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var maxScore by remember { mutableStateOf("5") }
    val sessionsNewestFirst = remember(sessions) {
        sessions.sortedWith(
            compareByDescending<LessonSessionDto> { it.date }
                .thenByDescending { it.title }
                .thenByDescending { it.id },
        )
    }

    val colors = GradeTheme.colors
    Dialog(onDismissRequest = onDismiss) {
        AppCard(modifier = Modifier.widthIn(min = 320.dp, max = 480.dp)) {
            Text("Новое ДЗ", color = colors.textPrimary, fontWeight = FontWeight.Bold, fontSize = 20.sp)
            MutedText("Выберите занятие и опишите задание", modifier = Modifier.padding(top = 4.dp, bottom = 10.dp))

            if (sessions.isEmpty()) {
                MutedText("Сначала добавьте занятие.")
            } else {
                Text("Занятие", color = YellowAccent, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                val sessionsScroll = rememberScrollState()
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 6.dp)
                        .horizontalScroll(sessionsScroll)
                        .horizontalScrollOnWheel(sessionsScroll),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    sessionsNewestFirst.forEach { session ->
                        val selected = session.id == selectedSessionId
                        Text(
                            text = "${formatDisplayDate(session.date)} · ${session.title}",
                            color = if (selected) Color.White else colors.textMuted,
                            fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
                            fontSize = 13.sp,
                            modifier = Modifier
                                .clip(RoundedCornerShape(10.dp))
                                .background(
                                    if (selected) SessionAccent.copy(alpha = 0.35f)
                                    else colors.nestedCard,
                                )
                                .border(
                                    1.dp,
                                    if (selected) SessionAccent else colors.border,
                                    RoundedCornerShape(10.dp),
                                )
                                .clickable { selectedSessionId = session.id }
                                .padding(horizontal = 12.dp, vertical = 8.dp),
                        )
                    }
                }

                AppTextField(title, { title = it }, "Название ДЗ", modifier = Modifier.padding(top = 12.dp))
                AppTextField(
                    description,
                    { description = it },
                    "Что задано (описание)",
                    modifier = Modifier.padding(top = 8.dp).heightIn(min = 96.dp),
                    singleLine = false,
                )
                AppTextField(maxScore, { maxScore = it }, "Макс. балл", modifier = Modifier.padding(top = 8.dp))
            }

            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                SecondaryButton(text = "Отмена", onClick = onDismiss, modifier = Modifier.weight(1f))
                PrimaryButton(
                    text = "Добавить",
                    enabled = title.isNotBlank() && selectedSessionId != null,
                    onClick = {
                        val sid = selectedSessionId ?: return@PrimaryButton
                        onConfirm(
                            sid,
                            title.trim(),
                            description.trim(),
                            maxScore.toDoubleOrNull() ?: 5.0,
                        )
                    },
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}
